import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import api from "../lib/api";
import { PORTAL_PLATFORMS } from "../lib/platforms";
import { LageBadge, ZoneChip, Stat, MockBadge, Spinner, Button } from "../components/ui";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, Tooltip, Cell,
} from "recharts";
import { ArrowLeft, Sparkles } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";

export default function MarketDetail() {
  const { t } = useI18n();
  const { code } = useParams();
  const nav = useNavigate();
  const { openChat } = useOutletContext() || {};
  const [m, setM] = useState(null);
  const [listings, setListings] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async (signal) => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get(`/markets/${code}`, { signal });
      setM(data.market);
      setListings(data.listings);
      setPlatforms(data.platforms || {});
    } catch (e) {
      if (axios.isCancel(e)) return;
      console.error("MarketDetail load failed", e);
      setErr(t("common.loadFailed"));
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  if (loading) return <div className="p-10"><Spinner /></div>;
  if (err || !m) return (
    <div className="p-10 text-center">
      <p className="text-sm text-signal-red font-mono">{err || t("common.loadFailed")}</p>
      <button onClick={() => load()} className="mt-3 text-xs font-mono uppercase tracking-widest2 text-signal-green hover:underline">
        {t("common.retry")}
      </button>
    </div>
  );

  const subs = m.lage_subscores;
  const radarData = [
    { axis: t("marketDetail.axisPopulation"), value: subs.population },
    { axis: t("marketDetail.axisAnchors"), value: subs.anchors },
    { axis: t("marketDetail.axisRail"), value: subs.rail },
    { axis: t("marketDetail.axisAbsorption"), value: subs.absorption },
    { axis: t("marketDetail.axisOwnMarket"), value: subs.own_market },
  ];

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-6 border-b border-border bg-grid">
        <button onClick={() => nav(-1)} className="overline hover:text-white inline-flex items-center gap-2">
          <ArrowLeft size={12} /> {t("marketDetail.backToMarkets")}
        </button>
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <LageBadge grade={m.lage_grade} />
          <span className="overline">{m.country}</span>
          <span className="overline text-text-muted">{m.code}</span>
          <MockBadge className="ml-2" />
        </div>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <h1 className="mt-3 font-heading text-4xl md:text-5xl font-black tracking-tight">{m.name}</h1>
          <Button onClick={openChat} variant="green">
            <Sparkles size={14} /> {t("marketDetail.askAiAbout", { code: m.code })}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 border-b border-border">
        <Stat label={t("marketDetail.lageScore")} value={`${m.lage_score}/100`} accent={m.lage_score >= 80 ? "green" : m.lage_score >= 60 ? "yellow" : "red"} />
        <Stat label={t("marketDetail.avgFaktor")} value={m.avg_faktor} sub={t("marketDetail.vsTarget")} />
        <Stat label={t("marketDetail.greenListings")} value={m.green_count} sub={t("marketDetail.ofTotal", { total: m.listing_count })} accent="green" />
        <Stat label={t("marketDetail.absorption")} value={`${m.absorption_days}d`} sub={t("marketDetail.kmMedian")} />
        <Stat label={t("marketDetail.cycle")} value={m.cycle} accent={m.cycle === "RISING" ? "green" : m.cycle === "FALLING" ? "red" : "white"} />
      </div>

      <div className="grid grid-cols-12">
        <section className="col-span-12 lg:col-span-6 border-r border-b border-border p-6">
          <div className="overline">{t("marketDetail.compositeLage")}</div>
          <h3 className="mt-1 font-heading text-2xl font-bold">{t("marketDetail.fiveSourceBlend")}</h3>
          <div className="mt-4 h-80">
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="#27272A" />
                <PolarAngleAxis dataKey="axis" stroke="#A1A1AA" tick={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <PolarRadiusAxis stroke="#27272A" tick={{ fontSize: 9, fill: "#71717A", fontFamily: "JetBrains Mono" }} domain={[0, 100]} />
                <Radar dataKey="value" stroke="#00FF66" fill="#00FF66" fillOpacity={0.25} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </section>

        <section className="col-span-12 lg:col-span-6 border-b border-border p-6">
          <div className="overline">{t("marketDetail.faktorDistribution")}</div>
          <h3 className="mt-1 font-heading text-2xl font-bold">{t("marketDetail.dealZones")}</h3>
          <div className="mt-4 h-80">
            <ResponsiveContainer>
              <BarChart data={faktorBuckets(listings)}>
                <XAxis dataKey="bucket" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <YAxis stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#27272A" }} />
                <Tooltip contentStyle={{ background: "#09090B", border: "1px solid #27272A", fontSize: 11, fontFamily: "JetBrains Mono" }} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="count">
                  {faktorBuckets(listings).map((b, i) => (
                    <Cell key={i} fill={b.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 flex gap-4 text-[10px] font-mono">
            <span className="text-signal-green">■ GREEN</span>
            <span className="text-signal-yellow">■ YELLOW</span>
            <span className="text-signal-red">■ RED</span>
          </div>
        </section>

        <section className="col-span-12 p-6">
          <div className="overline">{t("marketDetail.marketSignals")}</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-px border border-border bg-border">
            <Signal label={t("marketDetail.popTrend")} value={m.pop_trend ? m.pop_trend.toUpperCase() : "—"} accent={m.pop_trend === "decline" ? "red" : m.pop_trend === "stable" ? "white" : "green"} />
            <Signal label={t("marketDetail.neubauShare")} value={`${m.neubau_pct}%`} sub={t("marketDetail.ofListings")} accent={m.neubau_pct > 65 ? "red" : m.neubau_pct > 45 ? "yellow" : "green"} />
            <Signal label={t("marketDetail.medianPriceSqm")} value={`€${m.med_price_sqm.toLocaleString()}`} />
            <Signal label={t("marketDetail.medianRentSqm")} value={`€${m.med_rent_sqm}`} sub={t("marketDetail.netCalibrated")} />
          </div>
        </section>

        {(m.baujahr_median != null || m.altbau_share != null) && (
          <section className="col-span-12 px-6 pb-6">
            <div className="overline">{t("marketDetail.stockStructure")}</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-px border border-border bg-border">
              <Signal label={t("marketDetail.baujahrMedian")} value={m.baujahr_median != null ? m.baujahr_median : "—"} sub={t("marketDetail.baujahrMedianSub")} />
              <Signal
                label={t("marketDetail.altbauShare")}
                value={m.altbau_share != null ? `${m.altbau_share}%` : "—"}
                sub={m.altbau_share != null ? t("marketDetail.altbauShareSub") : undefined}
                accent={m.altbau_share != null && m.altbau_share >= 40 ? "yellow" : "white"}
              />
            </div>
          </section>
        )}

        {m.rent_active_count != null && (
          <section className="col-span-12 px-6 pb-6">
            <div className="overline">{t("marketDetail.rentLiquidity")}</div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-px border border-border bg-border">
              <Signal label={t("marketDetail.rentActive")} value={m.rent_active_count} sub={t("marketDetail.rentActiveSub")} />
              <Signal label={t("marketDetail.rentDom")} value={m.rent_median_dom != null ? `${m.rent_median_dom}d` : "—"} sub={t("marketDetail.rentDomSub")} />
              <Signal label={t("marketDetail.rentGt90")} value={m.rent_pct_gt_90d != null ? `${m.rent_pct_gt_90d}%` : "—"} accent={m.rent_pct_gt_90d != null ? (m.rent_pct_gt_90d > 25 ? "red" : m.rent_pct_gt_90d > 10 ? "yellow" : "green") : undefined} />
              <Signal label={t("marketDetail.rentMin")} value={m.rent_min_price != null ? `€${Number(m.rent_min_price).toLocaleString()}` : "—"} sub={m.rent_per_100k != null ? t("marketDetail.rentPer100kSub", { per100k: m.rent_per_100k }) : undefined} />
            </div>
          </section>
        )}

        <section className="col-span-12 px-6 pb-6">
          <div className="overline">{t("marketDetail.portalBreakdown")}</div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-px border border-border bg-border">
            {PORTAL_PLATFORMS.map((p) => {
              const count = platforms[p] || 0;
              const total = Object.values(platforms).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={p} className="bg-bg p-4" data-testid={`platform-cell-${p}`}>
                  <div className="overline">{p}</div>
                  <div className="num text-2xl mt-1 text-white">{count}</div>
                  <div className="text-[10px] font-mono text-text-muted mt-1">
                    {t("marketDetail.pctOfMarket", { pct })}
                  </div>
                  <div className="mt-2 h-1 bg-zinc-900 border border-border">
                    <div className="h-full bg-signal-green" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="col-span-12 px-6 pb-8">
          <div className="overline mb-3">{t("marketDetail.listingsIn", { market: m.name })}</div>
          <div className="border border-border">
            <div className="hidden md:grid md:grid-cols-12 text-[10px] font-mono uppercase tracking-widest2 text-text-muted border-b border-border bg-surface">
              <div className="col-span-1 p-3">{t("marketDetail.colZone")}</div>
              <div className="col-span-4 p-3">{t("marketDetail.colAddress")}</div>
              <div className="col-span-1 p-3 text-right">{t("marketDetail.colFaktor")}</div>
              <div className="col-span-2 p-3 text-right">{t("marketDetail.colPrice")}</div>
              <div className="col-span-1 p-3 text-right">m²</div>
              <div className="col-span-1 p-3 text-right">{t("marketDetail.colVsMarkt")}</div>
              <div className="col-span-2 p-3 text-right">{t("marketDetail.colCashflow")}</div>
            </div>
            {listings.sort((a, b) => a.faktor - b.faktor).map((l) => (
              <button
                key={l.id}
                onClick={() => nav(`/listings/${l.id}`)}
                className="block w-full text-left border-b border-border hover:bg-surfaceHover transition-colors md:grid md:grid-cols-12 md:items-center"
              >
                {/* Mobile: stacked card */}
                <div className="p-3 space-y-1.5 md:hidden">
                  <div className="flex items-center justify-between gap-2">
                    <ZoneChip zone={l.zone} />
                    <span className="num text-white">€{l.price.toLocaleString()}</span>
                  </div>
                  <div className="text-xs text-text-secondary truncate">{l.address}</div>
                  <div className="flex items-center justify-between text-[10px] font-mono text-text-muted">
                    <span>Faktor <span className="num text-white">{l.faktor.toFixed(1)}</span></span>
                    <span className={l.vs_markt_pct < 0 ? "text-signal-green" : "text-signal-red"}>{l.vs_markt_pct > 0 ? "+" : ""}{l.vs_markt_pct}% vs Markt</span>
                    <span className={l.monthly_cashflow >= 0 ? "text-signal-green" : "text-signal-red"}>
                      {l.monthly_cashflow >= 0 ? "+" : ""}€{l.monthly_cashflow}/mo
                    </span>
                  </div>
                </div>
                {/* Desktop: table row */}
                <div className="hidden md:contents">
                  <div className="col-span-1 p-3"><ZoneChip zone={l.zone} /></div>
                  <div className="col-span-4 p-3 text-xs text-text-secondary truncate">{l.address}</div>
                  <div className="col-span-1 p-3 num text-right text-white">{l.faktor.toFixed(1)}</div>
                  <div className="col-span-2 p-3 num text-right text-white">€{l.price.toLocaleString()}</div>
                  <div className="col-span-1 p-3 num text-right text-text-secondary">{l.size_m2}</div>
                  <div className={`col-span-1 p-3 num text-right ${l.vs_markt_pct < 0 ? "text-signal-green" : "text-signal-red"}`}>{l.vs_markt_pct > 0 ? "+" : ""}{l.vs_markt_pct}%</div>
                  <div className={`col-span-2 p-3 num text-right ${l.monthly_cashflow >= 0 ? "text-signal-green" : "text-signal-red"}`}>
                    {l.monthly_cashflow >= 0 ? "+" : ""}€{l.monthly_cashflow}/mo
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function Signal({ label, value, sub, accent = "white" }) {
  const cls = { white: "text-white", green: "text-signal-green", yellow: "text-signal-yellow", red: "text-signal-red" }[accent];
  return (
    <div className="bg-bg p-4">
      <div className="overline">{label}</div>
      <div className={`num text-2xl mt-1 ${cls}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono text-text-muted mt-1">{sub}</div>}
    </div>
  );
}

function faktorBuckets(listings) {
  const bins = [
    { bucket: "≤18", from: 0, to: 18, color: "#00FF66" },
    { bucket: "18-20", from: 18, to: 20, color: "#00FF66" },
    { bucket: "20-22", from: 20, to: 22, color: "#00FF66" },
    { bucket: "22-26", from: 22, to: 26, color: "#FFCC00" },
    { bucket: "26-30", from: 26, to: 30, color: "#FF3333" },
    { bucket: ">30", from: 30, to: 9999, color: "#FF3333" },
  ];
  return bins.map((b) => ({
    bucket: b.bucket,
    color: b.color,
    count: listings.filter((l) => l.faktor > b.from && l.faktor <= b.to).length,
  }));
}
